/**
 * «Заявки на оплату»: этапы словами, деньги и даты, форма заявки с проверкой
 * реквизитов, группировка списков по отделам и сотрудникам.
 *
 * Проверки повторяют серверные (`routes/pay_requests`), чтобы человек узнал об
 * ошибке до того, как счёт уйдёт на сервер ещё раз; решает всё равно сервер, и
 * его текст показываем как есть. Модуль без импортов значений — проверяется
 * node:test (`model.test.mjs`).
 */
import type {
  CreatePayRequestPayload,
  PayInvoiceFields,
  PayRequestDepartment,
  PayRequestMeta,
  PayRequestStage,
  PayRequestView,
} from '@shared/api';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

// ── Этапы ────────────────────────────────────────────────────────────────

/** Этапы для фильтра — подписи как у сервера (`PayStage::label`). */
export const STAGES: ReadonlyArray<{ code: PayRequestStage; label: string }> = [
  { code: 'at_financier', label: 'У Финансиста' },
  { code: 'deferred', label: 'Отложена' },
  { code: 'on_ceo', label: 'У Гендиректора' },
  { code: 'approved', label: 'Согласована, ждёт оплаты' },
  { code: 'partially_paid', label: 'Оплачена частично' },
  { code: 'paid', label: 'Оплачена' },
  { code: 'rejected', label: 'Отклонена' },
  { code: 'removed', label: 'Снята Финансистом' },
  { code: 'withdrawn', label: 'Отозвана' },
];

export function stageTone(stage: string): Tone {
  switch (stage) {
    case 'approved':
    case 'paid':
      return 'success';
    case 'deferred':
      return 'warning';
    case 'rejected':
      return 'danger';
    case 'at_financier':
    case 'on_ceo':
    case 'partially_paid':
      return 'info';
    default:
      return 'neutral';
  }
}

export function closingTone(closing: string): Tone {
  if (closing === 'awaiting') return 'warning';
  if (closing === 'provided') return 'success';
  return 'neutral';
}

/** Статус заявки Фабрики (блок «Как движется заявка»). */
export function finStatusTone(status: string): Tone {
  switch (status) {
    case 'approved':
    case 'exported_1c':
    case 'executed':
      return 'success';
    case 'deferred':
      return 'warning';
    case 'rejected':
      return 'danger';
    default:
      return 'info';
  }
}

/** Что сейчас с заявкой и чего ждать — одной фразой под статусом. */
export function stageHint(
  r: Pick<PayRequestView, 'stage' | 'partially_approved' | 'closing'>,
): string {
  switch (r.stage) {
    case 'at_financier':
      return 'Заявка у Финансиста — после проверки он передаст её Гендиректору на согласование.';
    case 'deferred':
      return 'Оплату отложили. Заявка остаётся у Финансиста — её вернут в реестр позже.';
    case 'on_ceo':
      return 'Заявка в реестре у Гендиректора — ждём согласования.';
    case 'approved':
      return r.partially_approved
        ? 'Согласовали часть суммы — что с остатком, видно в блоке «Как движется заявка».'
        : 'Оплату согласовали. Платёж уйдёт в банк по реестру.';
    case 'partially_paid':
      return 'Оплачена часть суммы — подробности в блоке «Как движется заявка».';
    case 'paid':
      return r.closing === 'awaiting'
        ? 'Счёт оплачен. Приложите закрывающие документы: УПД, акт или счёт-фактуру.'
        : 'Счёт оплачен.';
    case 'rejected':
      return 'Заявку отклонили. Если счёт всё же нужно оплатить, исправьте замечание и подайте новую заявку.';
    case 'removed':
      return 'Финансист убрал заявку из реестра платежей — уточните у него причину.';
    case 'withdrawn':
      return 'Заявку отозвали — Финансист её не оплатит.';
    default:
      return '';
  }
}

// ── Деньги и даты ────────────────────────────────────────────────────────

/** 1234.5 → «1 234,50 ₽»; копейки — только если они есть. */
export function formatRub(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '—';
  const kopecks = Math.abs(n - Math.round(n)) >= 0.005;
  const digits = kopecks ? 2 : 0;
  return `${n.toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ₽`;
}

/**
 * Сумма из поля ввода: «1 234 567,89», «1234567.89», «12 500 руб.». Пусто или
 * не число — `null`. Копейки округляются, как на сервере.
 */
export function parseMoney(raw: string): number | null {
  const s = raw
    .replace(/[\s  ]/g, '')
    .replace(/(₽|руб\.?|р\.)$/i, '')
    .replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/** Число в поле ввода: 1234.5 → «1 234,50» — его же разберёт `parseMoney`. */
export function moneyInput(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** «2026-10-28» → «28.10.2026». */
export function formatDate(day: string | null | undefined): string {
  if (!day) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : day;
}

/** Время события (ISO) → «28.10.2026» по местному времени. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('ru-RU');
}

/** Время события (ISO) → «28.10.2026, 14:05». */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** «Счёт № 15 от 20.10.2026»; ни номера, ни даты — пусто. */
export function invoiceLabel(r: Pick<PayRequestView, 'invoice_number' | 'invoice_date'>): string {
  const no = r.invoice_number.trim();
  const date = r.invoice_date ? `от ${formatDate(r.invoice_date)}` : '';
  if (!no) return date ? `Счёт ${date}` : '';
  return date ? `Счёт № ${no} ${date}` : `Счёт № ${no}`;
}

/** Сегодня по местному времени — «2026-10-28», как в `<input type="date">`. */
export function todayIso(now: Date = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

// ── Файлы ────────────────────────────────────────────────────────────────

/** Как счёт сервер принимает эти форматы (`FILE_TYPES` в `routes/pay_requests`). */
const INVOICE_EXT = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'heif',
  'xlsx',
  'xls',
  'docx',
  'doc',
  'odt',
  'ods',
  'rtf',
  'txt',
];
/** Закрывающие — ещё УПД из ЭДО: XML, подпись, архив. */
const CLOSING_EXT = [...INVOICE_EXT, 'xml', 'sig', 'p7s', 'zip'];
/** Эти сервер отдаёт для просмотра в браузере — открываем во вкладке. */
const INLINE_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'bmp'];

const toAccept = (list: string[]) => list.map((e) => `.${e}`).join(',');
export const INVOICE_ACCEPT = toAccept(INVOICE_EXT);
export const CLOSING_ACCEPT = toAccept(CLOSING_EXT);

/** Расширение как его берёт сервер: после последней точки, строчными. */
export function fileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0
    ? name
        .slice(i + 1)
        .trim()
        .toLowerCase()
    : '';
}

export const fitsAsInvoice = (name: string) => INVOICE_EXT.includes(fileExtension(name));
export const fitsAsClosing = (name: string) => CLOSING_EXT.includes(fileExtension(name));
export const opensInBrowser = (name: string) => INLINE_EXT.includes(fileExtension(name));

/** Предпросмотр счёта в форме: PDF — во фрейме, фото — картинкой (TIFF и HEIC браузер не покажет). */
export function previewKind(name: string): 'pdf' | 'image' | null {
  const ext = fileExtension(name);
  if (ext === 'pdf') return 'pdf';
  return ['jpg', 'jpeg', 'png', 'webp', 'bmp'].includes(ext) ? 'image' : null;
}

const MB = 1024 * 1024;
/** Закрывающих за раз и всех вместе — как на сервере. */
export const MAX_CLOSING_FILES = 10;
export const MAX_CLOSING_TOTAL_MB = 64;

type PickedFile = { name: string; size: number };

/** Почему файл не годится как счёт; `null` — годится. */
export function invoiceFileProblem(file: PickedFile, maxMb: number): string | null {
  if (!fitsAsInvoice(file.name)) {
    return `Файл «${file.name}» не подходит как счёт. Подойдут PDF, фото или скан (JPG, PNG, TIFF, HEIC), Excel или Word.`;
  }
  if (file.size === 0) return `Файл «${file.name}» пустой.`;
  if (file.size > maxMb * MB) {
    return `Файл «${file.name}» больше ${maxMb} МБ — сожмите скан или сохраните счёт в PDF.`;
  }
  return null;
}

/** Почему этот закрывающий не примут; `null` — примут. */
export function closingFileProblem(file: PickedFile, maxMb: number): string | null {
  if (!fitsAsClosing(file.name)) {
    return `Файл «${file.name}» не принимаем. Подойдут PDF, фото или скан, Excel, Word, XML из ЭДО с подписью, ZIP.`;
  }
  if (file.size === 0) return `Файл «${file.name}» пустой.`;
  if (file.size > maxMb * MB) return `Файл «${file.name}» больше ${maxMb} МБ.`;
  return null;
}

/** Пачка закрывающих целиком: не больше 10 файлов и 64 МБ за раз. */
export function closingBatchProblem(files: PickedFile[]): string | null {
  if (files.length > MAX_CLOSING_FILES) {
    return `За раз — не больше ${MAX_CLOSING_FILES} файлов. Остальные приложите следующим заходом.`;
  }
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > MAX_CLOSING_TOTAL_MB * MB) {
    return `Файлы вместе больше ${MAX_CLOSING_TOTAL_MB} МБ — приложите их в несколько приёмов.`;
  }
  return null;
}

// ── Реквизиты ────────────────────────────────────────────────────────────

/** Без пробелов: так поле хранит и проверяет сервер. */
export function compact(raw: string): string {
  return raw.replace(/\s+/g, '');
}

/** Длина в знаках, как её считает сервер (не в UTF-16). */
export function charCount(s: string): number {
  return Array.from(s).length;
}

/** ИНН с верным контрольным числом: 10 цифр у организации, 12 — у ИП. */
export function innValid(inn: string): boolean {
  if (!/^(\d{10}|\d{12})$/.test(inn) || /^0+$/.test(inn)) return false;
  const d = Array.from(inn, Number);
  const check = (coef: number[], at: number) =>
    (coef.reduce((sum, c, i) => sum + c * d[i], 0) % 11) % 10 === d[at];
  return d.length === 10
    ? check([2, 4, 10, 3, 5, 9, 4, 6, 8], 9)
    : check([7, 2, 4, 10, 3, 5, 9, 4, 6, 8], 10) && check([3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8], 11);
}

/** Ключ счёта: 23 цифры с весами 7-1-3, сумма кратна 10. */
function keyOk(digits23: string): boolean {
  if (!/^\d{23}$/.test(digits23)) return false;
  const w = [7, 1, 3];
  let sum = 0;
  for (let i = 0; i < 23; i += 1) sum += Number(digits23[i]) * w[i % 3];
  return sum % 10 === 0;
}

const isDigits = (s: string, n: number) => s.length === n && /^\d+$/.test(s);

/** Расчётный счёт сходится с БИК (последние три цифры БИК + счёт). */
export function settlementOk(account: string, bik: string): boolean {
  return isDigits(bik, 9) && isDigits(account, 20) && keyOk(bik.slice(6, 9) + account);
}

/** Корреспондентский счёт сходится с БИК («0» + 5–6 цифры БИК + счёт). */
export function corrOk(account: string, bik: string): boolean {
  return isDigits(bik, 9) && isDigits(account, 20) && keyOk(`0${bik.slice(4, 6)}${account}`);
}

// ── Форма заявки ─────────────────────────────────────────────────────────

/** Поля формы — как ввёл человек (суммы строками). */
export interface PayForm {
  service_id: string;
  counterparty_name: string;
  counterparty_inn: string;
  counterparty_kpp: string;
  invoice_number: string;
  invoice_date: string;
  amount: string;
  vat_amount: string;
  without_vat: boolean;
  purpose: string;
  category: string;
  urgent: boolean;
  desired_pay_date: string;
  project_id: string;
  comment: string;
  recipient_account_number: string;
  recipient_bik: string;
  recipient_bank_name: string;
  recipient_corr_account: string;
}

export type PayFormField = keyof PayForm;
export type PayFormErrors = Partial<Record<PayFormField, string>>;

export const EMPTY_FORM: PayForm = {
  service_id: '',
  counterparty_name: '',
  counterparty_inn: '',
  counterparty_kpp: '',
  invoice_number: '',
  invoice_date: '',
  amount: '',
  vat_amount: '',
  without_vat: false,
  purpose: '',
  category: '',
  urgent: false,
  desired_pay_date: '',
  project_id: '',
  comment: '',
  recipient_account_number: '',
  recipient_bik: '',
  recipient_bank_name: '',
  recipient_corr_account: '',
};

/** Банковские реквизиты — свёрнутый блок формы. */
export const BANK_FIELDS: readonly PayFormField[] = [
  'recipient_account_number',
  'recipient_bik',
  'recipient_bank_name',
  'recipient_corr_account',
];

/**
 * Поля из распознанного счёта поверх `base` (отдел, статья и прочее, чего в
 * счёте нет, остаются). `filled` — что заполнили по счёту: эти поля
 * проверяются сразу, не дожидаясь отправки.
 */
export function formFromInvoice(
  f: PayInvoiceFields,
  base: PayForm,
): { form: PayForm; filled: PayFormField[] } {
  const form: PayForm = { ...base };
  const filled: PayFormField[] = [];
  const put = (field: Exclude<PayFormField, 'without_vat' | 'urgent'>, value: string | null) => {
    const v = (value ?? '').trim();
    if (!v) return;
    form[field] = v;
    filled.push(field);
  };
  put('counterparty_name', f.counterparty_name);
  put('counterparty_inn', f.counterparty_inn);
  put('counterparty_kpp', f.counterparty_kpp);
  put('invoice_number', f.invoice_number);
  put('invoice_date', f.invoice_date);
  put('amount', moneyInput(f.amount));
  if (f.without_vat) {
    form.without_vat = true;
    form.vat_amount = '';
  } else {
    put('vat_amount', moneyInput(f.vat_amount));
  }
  put('purpose', f.purpose);
  put('recipient_account_number', f.recipient_account_number);
  put('recipient_bik', f.recipient_bik);
  put('recipient_bank_name', f.recipient_bank_name);
  put('recipient_corr_account', f.recipient_corr_account);
  return { form, filled };
}

/** Отдел в форме: не нужен (справочника нет), свой единственный или выбрать. */
export type DepartmentChoice =
  | { kind: 'none' }
  | { kind: 'fixed'; title: string }
  | { kind: 'choose'; options: PayRequestDepartment[]; preset: string };

/**
 * Один свой отдел — сервер подставит его сам (показываем, не спрашиваем).
 * Несколько — выбор из своих; ни одного — из всех; видящий все выбирает любой.
 */
export function departmentChoice(
  meta: Pick<PayRequestMeta, 'departments' | 'my_departments' | 'sees_all'>,
): DepartmentChoice {
  const { departments, my_departments: mine, sees_all: seesAll } = meta;
  if (departments.length === 0) return { kind: 'none' };
  if (!seesAll && mine.length === 1) {
    return { kind: 'fixed', title: departments.find((d) => d.id === mine[0])?.title ?? '' };
  }
  const own = departments.filter((d) => mine.includes(d.id));
  const options = seesAll || own.length === 0 ? departments : own;
  return { kind: 'choose', options, preset: mine.length === 1 ? mine[0] : '' };
}

/** Что не так в форме — то же, на что откажет сервер. Пусто — можно отправлять. */
export function checkForm(
  form: PayForm,
  opts: { needDepartment: boolean; maxPurpose: number; today: string },
): PayFormErrors {
  const e: PayFormErrors = {};
  if (opts.needDepartment && !form.service_id) e.service_id = 'Выберите отдел, от которого заявка';

  const name = form.counterparty_name.trim();
  if (!name) e.counterparty_name = 'Укажите поставщика — кому платим';
  else if (charCount(name) > 255) e.counterparty_name = 'Название поставщика длиннее 255 знаков';

  const inn = compact(form.counterparty_inn);
  if (inn && !/^(\d{10}|\d{12})$/.test(inn)) {
    e.counterparty_inn = 'ИНН — 10 цифр у организации или 12 у ИП';
  } else if (inn && !innValid(inn)) {
    e.counterparty_inn = 'ИНН с ошибкой — не сходится контрольное число, проверьте цифры';
  }
  const kpp = compact(form.counterparty_kpp);
  if (kpp && !/^[0-9A-Za-z]{9}$/.test(kpp)) e.counterparty_kpp = 'КПП — 9 знаков';

  if (charCount(form.invoice_number.trim()) > 60)
    e.invoice_number = 'Номер счёта длиннее 60 знаков';

  const amount = parseMoney(form.amount);
  if (!form.amount.trim()) e.amount = 'Укажите сумму к оплате';
  else if (amount == null) e.amount = 'Сумма — числом, например 12 500,00';
  else if (amount <= 0) e.amount = 'Сумма к оплате должна быть больше нуля';
  else if (amount > 1e12) e.amount = 'Сумма к оплате слишком большая — проверьте';

  if (!form.without_vat && form.vat_amount.trim()) {
    const vat = parseMoney(form.vat_amount);
    if (vat == null) e.vat_amount = 'НДС — числом, например 2 083,33';
    else if (amount != null && amount > 0 && vat > amount)
      e.vat_amount = 'НДС больше суммы к оплате';
  }

  const purpose = form.purpose.trim();
  if (!purpose) e.purpose = 'Укажите назначение платежа — за что платим';
  else if (charCount(purpose) > opts.maxPurpose) {
    e.purpose = `Назначение платежа — не больше ${opts.maxPurpose} знаков (так в платёжке)`;
  }

  if (form.desired_pay_date && form.desired_pay_date < opts.today) {
    e.desired_pay_date = 'Желаемая дата оплаты уже прошла';
  }
  if (charCount(form.comment.trim()) > 2000) e.comment = 'Комментарий длиннее 2000 знаков';

  const bik = compact(form.recipient_bik);
  const bikOk = isDigits(bik, 9);
  if (bik && !bikOk) e.recipient_bik = 'БИК — 9 цифр';
  const account = compact(form.recipient_account_number);
  if (account) {
    if (!isDigits(account, 20)) e.recipient_account_number = 'Расчётный счёт — 20 цифр';
    else if (!bik) e.recipient_bik = 'Укажите БИК банка к расчётному счёту';
    else if (bikOk && !settlementOk(account, bik)) {
      e.recipient_account_number = 'Расчётный счёт не сходится с БИК — проверьте цифры';
    }
  }
  const corr = compact(form.recipient_corr_account);
  if (corr) {
    if (!isDigits(corr, 20)) e.recipient_corr_account = 'Корреспондентский счёт — 20 цифр';
    else if (bikOk && !corrOk(corr, bik)) {
      e.recipient_corr_account = 'Корреспондентский счёт не сходится с БИК — проверьте цифры';
    }
  }
  return e;
}

/** Данные заявки для сервера. Пустое — `null`; номера счетов — без пробелов. */
export function payloadFromForm(
  form: PayForm,
  opts: { sendDepartment: boolean },
): CreatePayRequestPayload {
  const text = (s: string) => s.trim() || null;
  const digits = (s: string) => compact(s) || null;
  return {
    service_id: opts.sendDepartment && form.service_id ? form.service_id : null,
    counterparty_name: form.counterparty_name.trim(),
    counterparty_inn: digits(form.counterparty_inn),
    counterparty_kpp: digits(form.counterparty_kpp),
    invoice_number: text(form.invoice_number),
    invoice_date: form.invoice_date || null,
    amount: parseMoney(form.amount) ?? 0,
    vat_amount: form.without_vat ? 0 : parseMoney(form.vat_amount),
    purpose: form.purpose.trim(),
    ...(form.category ? { category: form.category } : {}),
    urgent: form.urgent,
    desired_pay_date: form.desired_pay_date || null,
    project_id: form.project_id || null,
    comment: text(form.comment),
    recipient_account_number: digits(form.recipient_account_number),
    recipient_bik: digits(form.recipient_bik),
    recipient_bank_name: text(form.recipient_bank_name),
    recipient_corr_account: digits(form.recipient_corr_account),
  };
}

// ── Группировка списков ──────────────────────────────────────────────────

export const NO_DEPARTMENT = 'Без отдела';

export interface PersonGroup {
  key: string;
  name: string;
  items: PayRequestView[];
  /** Сумма к оплате. */
  amount: number;
  /** Оплачено. */
  paid: number;
}

export interface DepartmentGroup {
  key: string;
  name: string;
  people: PersonGroup[];
  count: number;
  amount: number;
  paid: number;
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'ru');
const oldestDebt = (items: PayRequestView[]) =>
  items.reduce((max, r) => Math.max(max, r.days_since_paid ?? 0), 0);

/**
 * По отделам, в отделе — по сотрудникам. Отделы — в порядке справочника
 * «Службы» (`order` — id отделов), «Без отдела» — в конце. `by: 'debt'` —
 * для «Нет закрывающих»: сотрудники и их заявки от самой давней оплаты, с кого
 * спросить в первую очередь; иначе сотрудники по алфавиту, заявки как пришли.
 */
export function groupByDepartment(
  items: PayRequestView[],
  order: string[] = [],
  by: 'name' | 'debt' = 'name',
): DepartmentGroup[] {
  // Ключ отдела — id из «Служб»; отдел без id — по названию; '' — без отдела.
  const departments = new Map<
    string,
    { name: string; serviceId: string | null; rows: PayRequestView[] }
  >();
  for (const r of items) {
    const name = r.department_name.trim();
    const key = r.service_id ?? (name ? `name:${name}` : '');
    const group = departments.get(key) ?? {
      name: name || NO_DEPARTMENT,
      serviceId: r.service_id,
      rows: [],
    };
    group.rows.push(r);
    departments.set(key, group);
  }

  // Место отдела: по справочнику, неизвестные — за ними, «Без отдела» — последним.
  const rank = (key: string, serviceId: string | null) => {
    if (!key) return Number.MAX_SAFE_INTEGER;
    const i = serviceId ? order.indexOf(serviceId) : -1;
    return i >= 0 ? i : order.length;
  };

  const result: { group: DepartmentGroup; rank: number }[] = [];
  for (const [key, group] of departments) {
    const people = new Map<string, PersonGroup>();
    for (const r of group.rows) {
      const personKey = r.created_by ?? `name:${r.initiator_name}`;
      const person = people.get(personKey) ?? {
        key: personKey,
        name: r.initiator_name || 'Сотрудник',
        items: [],
        amount: 0,
        paid: 0,
      };
      person.items.push(r);
      person.amount += r.amount;
      person.paid += r.paid_total;
      people.set(personKey, person);
    }
    const list = [...people.values()];
    if (by === 'debt') {
      for (const p of list) {
        p.items.sort((a, b) => (b.days_since_paid ?? 0) - (a.days_since_paid ?? 0));
      }
      list.sort((a, b) => oldestDebt(b.items) - oldestDebt(a.items) || byName(a, b));
    } else {
      list.sort(byName);
    }
    result.push({
      group: {
        key: key || 'none',
        name: group.name,
        people: list,
        count: group.rows.length,
        amount: list.reduce((s, p) => s + p.amount, 0),
        paid: list.reduce((s, p) => s + p.paid, 0),
      },
      rank: rank(key, group.serviceId),
    });
  }
  return result.sort((a, b) => a.rank - b.rank || byName(a.group, b.group)).map((r) => r.group);
}
